import React, { useState, useEffect } from 'react';
import '../styles/global.css';
import { getSimpleCatalog } from '../services/CatalogService.js';

const SimpleCatalogLibrary = () => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [genreFilter, setGenreFilter] = useState('all');
  const [bpmRange, setBpmRange] = useState({ min: 60, max: 180 });

  useEffect(() => {
    fetchCatalogData();
  }, []);

    const fetchCatalogData = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('🎵 SimpleCatalogLibrary: Loading catalog...');

        const { tracks: apiTracks } = await getSimpleCatalog();
        console.log('📊 SimpleCatalogLibrary: Raw API response:', apiTracks);

        if (Array.isArray(apiTracks) && apiTracks.length > 0) {
          setTracks(apiTracks);
          console.log('✅ SimpleCatalogLibrary: Loaded', apiTracks.length, 'tracks');
          console.log('🎼 Sample track:', apiTracks[0]);
        } else {
          console.warn('⚠️ SimpleCatalogLibrary: No tracks in API response');
          setError('No tracks found in API response');
        }
      } catch (err) {
        console.error('❌ SimpleCatalogLibrary: Fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

  const copyTrackInfo = (track) => {
    const info = `${track.track_title}
Artist: ${track.artist}
Genre: ${track.genre}
BPM: ${track.bpm}
Duration: ${track.duration}
Key: ${track.key || 'N/A'}
Energy: ${track.energy_level || 'N/A'}
Danceability: ${track.danceability || 'N/A'}
Sync Category: ${track.sync_category || 'N/A'}
Publisher: decoded music | AHA LLC
Preview: ${track.preview_url || 'Available on request'}`;
    
    navigator.clipboard.writeText(info);
    alert('Track info copied to clipboard!');
  };

  const generateEmailPitch = (track) => {
    const email = `Subject: Sync Opportunity - "${track.track_title}" by Rue de Vivre

Dear Music Supervisor,

I'd like to present "${track.track_title}" from Rue de Vivre's professional catalog:

🎵 TRACK DETAILS:
• Title: ${track.track_title}
• Artist: ${track.artist}
• Genre: ${track.genre}
• BPM: ${track.bpm}
• Duration: ${track.duration}
• Key: ${track.key}
• Perfect for: ${track.sync_category}

📊 AUDIO ANALYSIS:
• Energy Level: ${track.energy_level}/100
• Danceability: ${track.danceability}/100
• Mood (Valence): ${track.valence}/100
• Rhythm Stability: ${Math.round((track.rhythm_stability || 0) * 100)}%

🎯 SYNC USAGE:
This track is professionally analyzed and categorized for ${track.sync_category.toLowerCase()} applications.

📦 AVAILABLE ASSETS:
• Master recording (24-bit WAV)
• Instrumental version
• Custom edits (15", 30", 60")
• Stems package available

🔗 PREVIEW:
${track.preview_url || 'Preview available upon request'}

💼 LICENSING:
Competitive rates • Same-day delivery • Full clearance
Publisher: decoded music | AHA LLC

Contact: ops@decodedmusic.com
Website: www.decodedmusic.com

Best regards,
Rue de Vivre Team`;
    
    navigator.clipboard.writeText(email);
    alert('Email pitch copied to clipboard!');
  };

  const copyAllTracks = () => {
    const allInfo = tracks.map(track => 
      `${track.track_title} | ${track.artist} | ${track.genre} | ${track.bpm} BPM | ${track.duration}`
    ).join('\n');
    
    navigator.clipboard.writeText(allInfo);
    alert(`${tracks.length} tracks copied to clipboard!`);
  };

  // Filter tracks based on search and filters
  const filteredTracks = tracks.filter(track => {
    const matchesSearch = !searchTerm || 
      track.track_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      track.artist.toLowerCase().includes(searchTerm.toLowerCase()) ||
      track.genre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      track.sync_category.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesGenre = genreFilter === 'all' || track.genre === genreFilter;
    const matchesBpm = track.bpm >= bpmRange.min && track.bpm <= bpmRange.max;
    
    return matchesSearch && matchesGenre && matchesBpm;
  });

  const uniqueGenres = [...new Set(tracks.map(track => track.genre))].sort();

  if (loading) {
    return (
      <div className="professional-loading">
        <div className="loading-spinner"></div>
        <h2>Loading Catalog...</h2>
        <p>Fetching tracks from S3 with librosa metadata...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="professional-error">
        <h2>Error Loading Catalog</h2>
        <p>{error}</p>
        <button className="professional-button" onClick={fetchCatalogData}>Retry</button>
      </div>
    );
  }

  return (
    <div className="professional-container">
      <div className="professional-header">
        <h1>🎵 Rue de Vivre Catalog</h1>
        <p>
          {filteredTracks.length} of {tracks.length} tracks with librosa audio analysis
        </p>
        <button 
          onClick={copyAllTracks}
          className="professional-button"
        >
          📋 Copy All Tracks
        </button>
      </div>

      {/* Search and Filter Controls */}
      <div className="professional-card">
        <div className="professional-card-header">
          <h3 className="professional-card-title">🔍 Search & Filter</h3>
        </div>
        <div className="professional-card-content">
          <div className="professional-grid-3">
            <input
              type="text"
              placeholder="Search tracks, artists, genres, sync categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="professional-search"
            />
            
            <select
              value={genreFilter}
              onChange={(e) => setGenreFilter(e.target.value)}
              className="genre-filter"
            >
              <option value="all">All Genres</option>
              {uniqueGenres.map(genre => (
                <option key={genre} value={genre}>{genre}</option>
              ))}
            </select>
            
            <div className="bpm-filter-section">
              <label>BPM:</label>
              <input
                type="range"
                min="60"
                max="180"
                value={bpmRange.min}
                onChange={(e) => setBpmRange({...bpmRange, min: parseInt(e.target.value)})}
                className="bpm-slider"
              />
              <span>{bpmRange.min}-{bpmRange.max}</span>
              <input
                type="range"
                min="60"
                max="180"
                value={bpmRange.max}
                onChange={(e) => setBpmRange({...bpmRange, max: parseInt(e.target.value)})}
                className="bmp-slider"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="professional-grid-auto">
        {filteredTracks.map((track, index) => (
          <div 
            key={track.id || index}
            className="professional-track-card"
          >
            <div className="track-primary">
              <div className="track-metadata">
                <h3 className="track-title">
                  {track.track_title}
                </h3>
                <div className="track-actions-professional">
                  <button
                    onClick={() => copyTrackInfo(track)}
                    className="action-btn secondary"
                  >
                    📋 Copy Info
                  </button>
                  <button
                    onClick={() => generateEmailPitch(track)}
                    className="action-btn primary"
                  >
                    📧 Email Pitch
                  </button>
                </div>
              </div>
            </div>

            <div className="audio-player">
              <audio controls style={{width: '100%', marginBottom: '10px'}}>
                <source src={`https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/playlists/${track.id || track.track_id}.mp3`} type="audio/mpeg" />
                <source src={`https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/playlists/${track.id || track.track_id}.wav`} type="audio/wav" />
                Your browser does not support the audio element.
              </audio>
            </div>
            
            <div className="track-specs-professional">
              <span className="bpm-badge">{track.bpm} BPM</span>
              <span className="key-badge">{track.key}</span>
              <span className="genre-badge">{track.genre}</span>
              <span className="duration-badge">{track.duration}</span>
            </div>
            
            <div className="librosa-metrics">
              <span className="energy-badge">Energy: {track.energy_level}</span>
              <span className="dance-badge">Dance: {track.danceability}</span>
              <span className="valence-badge">Mood: {track.valence}</span>
              {track.rhythm_stability && (
                <span className="energy-badge">Rhythm: {Math.round(track.rhythm_stability * 100)}%</span>
              )}
            </div>
            
            <div className="professional-list">
              <div className="professional-list-item">
                <span className="professional-list-item-title">Artist:</span>
                <span className="professional-list-item-value">{track.artist}</span>
              </div>
              {track.complexity && (
                <div className="professional-list-item">
                  <span className="professional-list-item-title">Complexity:</span>
                  <span className="professional-list-item-value">{Math.round(track.complexity)}</span>
                </div>
              )}
              {track.spectral_centroid && (
                <div className="professional-list-item">
                  <span className="professional-list-item-title">Brightness:</span>
                  <span className="professional-list-item-value">{Math.round(track.spectral_centroid)} Hz</span>
                </div>
              )}
            </div>

            {track.sync_category && (
              <div className="sync-category">
                <span className="sync-badge">{track.sync_category}</span>
              </div>
            )}

            {track.publisher && (
              <div className="professional-list-item">
                <span className="professional-list-item-title">Publisher:</span>
                <span className="professional-list-item-value">{track.publisher}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredTracks.length === 0 && tracks.length > 0 && (
        <div className="no-results-professional">
          <h3>No tracks match your search</h3>
          <p>Try adjusting your search terms or filters.</p>
        </div>
      )}
      
      {tracks.length === 0 && !loading && (
        <div className="no-results-professional">
          <h3>No tracks found</h3>
          <p>The catalog appears to be empty or unavailable.</p>
        </div>
      )}

      <footer className="catalog-footer-professional">
        <div className="footer-content">
          <div>
            <h3>Professional Licensing</h3>
            <p>📧 ops@decodedmusic.com</p>
            <p>🌐 www.decodedmusic.com</p>
          </div>
          <div>
            <h3>Audio Analysis</h3>
            <p>All tracks analyzed with librosa</p>
            <p>Professional metadata included</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SimpleCatalogLibrary;